<template>
  <nav class="breadcrumbs" data-test="search-breadcrumbs">
    <search-breadcrumbs-generator
      v-if="breadcrumbItems"
      :breadcrumbs="breadcrumbItems"
      :with-counts="withCounts"
      @update-rendered-url="
        ({ key, label, url }) => updateRenderedUrl(key, label, url)
      "
    >
      <pu-link
        v-bind="homeBreadcrumb"
        exclude-type="fullMinusType"
        :non-seo="true"
      >
        <translate>Home</translate>
      </pu-link>
      <span class="crumb">&rsaquo;</span>
    </search-breadcrumbs-generator>
    <nuxt-to-meta v-if="showSchema" :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

/* TODO: Add proper typing to resolve type safety warnings */

import { computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Breadcrumb } from '~/apps/search/schemas'
import type { BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { SearchFilters } from '~/types/search'
import { getFacetRules } from '~/utility/facets'
import { CATEGORIES } from '../../apps/pu-links/simpleCategories'
import { HomepageLinkGenerator } from '../../apps/url/generators/HomepageLinkGenerator'
import { isGlamping } from '../../utility/glamping'
import { computeBreadcrumbs } from '~/apps/breadcrumbs/computeBreadcrumbs'

const props = defineProps<{
  breadcrumbs: Breadcrumb[]
  showSchema?: boolean
  counts?: Record<string, number>
  withCounts?: boolean
}>()

const searchStore = useSearchStoreSetup(usePinia())
const route = useRoute()
const config = useRuntimeConfig()
const langCode = (route.params.lang as string) || 'en-gb'

const isGlampingCategory = computed(() => {
  return isGlamping(searchStore.searchFilters.categoryIds || [])
})

const isSeo = computed(() => {
  const searchFilters = searchStore.searchFilters
  // FIXED: Properly compare glamping categories using existing isGlamping helper
  const isGlampingCategories = isGlamping(searchFilters.categoryIds || [])

  // Non-SEO if has dates (arrival/departure)
  if (searchFilters.dates?.arrive || searchFilters.dates?.depart) {
    return false
  }

  // FIXED: Non-SEO only if has NON-DEFAULT party size params
  // Default party params (adults: 2, childAges: []) should not affect SEO status
  const hasNonDefaultPartyParams =
    searchFilters.party &&
    ((searchFilters.party.adults !== undefined &&
      searchFilters.party.adults !== 2) ||
      (searchFilters.party.childAges !== undefined &&
        searchFilters.party.childAges.length > 0))

  if (hasNonDefaultPartyParams) {
    return false
  }

  // Non-SEO if has more than 1 facet
  if (searchFilters.facets?.length && searchFilters.facets.length > 1) {
    return false
  }

  // Non-SEO if has multiple categories (unless it's the glamping combination)
  if ((searchFilters.categoryIds?.length ?? 0) > 1 && !isGlampingCategories) {
    return false
  }

  return true
})

const baseLangPrefix = computed(() => {
  return langCode === 'en-gb' ? '/' : `/${langCode}/`
})

function getBaseUrl(useSearch: boolean) {
  if (!isSeo.value || useSearch) {
    return `${baseLangPrefix.value}search`
  }
  const categoryIds = searchStore.searchFilters.categoryIds
  if (categoryIds?.length === 1) {
    const rootSlug = CATEGORIES[categoryIds[0]]
    return `${baseLangPrefix.value}${rootSlug}`
  }
  if (isGlampingCategory.value) {
    return `${baseLangPrefix.value}glamping`
  }
  return `${baseLangPrefix.value}campsites`
}

const searchFilters = computed((): SearchFilters => {
  const filters = searchStore.searchFilters
  return {
    ...filters,
    hierarchyPath: filters.hierarchyPath ?? undefined,
  }
})

const isDatedSearch = computed(() => {
  return Boolean(searchFilters.value?.dates?.arrive)
})

// For dated searches, use country-level hierarchy path for count queries.
// The backend returns counts for the scoped path and its descendants only — not
// ancestors. Using city-level returns empty counts when there are no results for
// those dates at that city. Using country-level ensures all ancestors appear.
// For undated pages, keep the original path (already working).
const countsHierarchyPath = computed(() => {
  if (isDatedSearch.value) {
    const countryBreadcrumb = props.breadcrumbs.find(
      (b) => b.hierarchyLevel === 0,
    )
    if (countryBreadcrumb?.hierarchyPath) {
      return countryBreadcrumb.hierarchyPath
    }
  }
  return searchFilters.value.hierarchyPath
})

const breadcrumbCountsSearchFilters = computed(() => {
  return {
    ...searchFilters.value,
    facets: [],
    hierarchyPath: countsHierarchyPath.value,
  }
})

const { breadcrumbCounts } = useBreadcrumbCounts(
  breadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

// Faceted breadcrumb counts (WITH facets included)
// Used to determine if breadcrumb should be linkable - don't link to zero-result pages
const facetedBreadcrumbCountsSearchFilters = computed(() => {
  return { ...searchFilters.value, hierarchyPath: countsHierarchyPath.value }
})

const { breadcrumbCounts: facetedBreadcrumbCounts } = useBreadcrumbCounts(
  facetedBreadcrumbCountsSearchFilters,
) as { breadcrumbCounts: Ref<BreadcrumbCounts> }

const { data: facetRules } = await useAsyncData(
  `search-breadcrumb-facet-rules-${JSON.stringify(searchFilters.value)}`,
  async () => {
    if (!searchFilters.value.facets?.length) {
      return []
    }
    const rules = await Promise.all(
      searchFilters.value.facets.map((facet) => getFacetRules(facet, langCode)),
    )
    return rules.flat()
  },
  {
    watch: [searchFilters],
    server: true,
    lazy: import.meta.client,
  },
)

const hierarchyCounts = computed(
  () => breadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

const facetedHierarchyCounts = computed(
  () => facetedBreadcrumbCounts.value?.bookableFacetHierarchy ?? {},
)

// Total counts (free + bookable) — used for country-level linkability
// so countries with only free listings still get breadcrumb links
const totalHierarchyCounts = computed(
  () => facetedBreadcrumbCounts.value?.facetHierarchy ?? {},
)

const homeBreadcrumb = computed(() => {
  const homeLinkGenerator = new HomepageLinkGenerator()
  return homeLinkGenerator.getUrlData(searchFilters.value)
})

const breadcrumbItems = computed(() => {
  return computeBreadcrumbs({
    breadcrumbs: props.breadcrumbs,
    counts: props.counts,
    searchFilters: searchFilters.value,
    facetRules: facetRules.value ?? null,
    hierarchyCounts: hierarchyCounts.value,
    facetedHierarchyCounts: facetedHierarchyCounts.value,
    totalHierarchyCounts: totalHierarchyCounts.value,
    isGlampingCategory: isGlampingCategory.value,
    isDatedSearch: isDatedSearch.value,
    getBaseUrl,
  })
})

// Map keyed by unique identifier (hierarchyPath or label), storing { label, url }
const renderedUrls = ref(new Map<string, { label: string; url: string }>())

const isMounted = ref(true)
onUnmounted(() => {
  isMounted.value = false
})

const updateRenderedUrl = (key: string, label: string, url: string) => {
  if (!isMounted.value) return
  const existing = renderedUrls.value.get(key)
  if (existing && existing.url === url && existing.label === label) return
  renderedUrls.value.set(key, { label, url })
}

const head = computed(() => {
  if (props.showSchema) {
    const crumbs = breadcrumbItems.value
    const lastBreadcrumb = crumbs[crumbs.length - 1]

    const allBreadcrumbs = [
      {
        label: 'Home',
        id: `https://${config.public.envUrl}${homeBreadcrumb.value.path}`,
      },
      // Include all linkable breadcrumbs except the last (handled separately below)
      ...crumbs.slice(0, -1).flatMap((crumb) => {
        if (!crumb.linkable || !crumb.hierarchyPath) return []
        const key = crumb.hierarchyPath || crumb.label
        const entry = renderedUrls.value.get(key)
        // Use rendered URL if available, otherwise fall back to computed URL
        // (facet tier URLs come from computeBreadcrumbs, not pu-link)
        const url = entry?.url ?? crumb.url
        return [
          {
            label: crumb.label,
            id: `https://${config.public.envUrl}${url}`,
          },
        ]
      }),
    ]
    // Always add the last breadcrumb (current page) to JSON-LD — Google expects
    // the full trail including the current page, even though it's not a link in the UI
    if (lastBreadcrumb) {
      let finalUrl: string
      const lastKey = lastBreadcrumb.hierarchyPath || lastBreadcrumb.label

      // Use rendered URL from pu-link if available (client-side), otherwise compute it (SSR)
      const lastEntry = renderedUrls.value.get(lastKey)
      if (lastEntry) {
        finalUrl = lastEntry.url
      } else {
        // Fallback: Apply same pu-link logic for SSR
        finalUrl = lastBreadcrumb.url
        const facets = searchStore.searchFilters.facets || []

        if (facets.length > 0 && !lastBreadcrumb.removeFacetQuery) {
          if (lastBreadcrumb.nonSeo) {
            // Query param format: ?facet=...
            const separator = finalUrl.includes('?') ? '&' : '?'
            finalUrl = `${finalUrl}${separator}facet=${facets.join(',')}`
          } else {
            // SEO URL format: /-/facet/
            const facetPath = `/-/${facets[0]}/`
            if (!finalUrl.includes(facetPath)) {
              finalUrl = `${finalUrl.replace(/\/$/, '')}${facetPath}`
            }
          }
        }
      }

      allBreadcrumbs.push({
        label: lastBreadcrumb.label,
        id: `https://${config.public.envUrl}${finalUrl}`,
      })
    }
    return {
      script: [
        {
          hid: 'ldjson-schema-search-breadcrumbs',
          type: 'application/ld+json',
          body: true,
          json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: allBreadcrumbs.map((breadcrumb, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@id': breadcrumb.id,
                name: breadcrumb.label,
              },
            })),
          },
        },
      ],
    }
  }
  return {}
})
</script>

<style>
@import url('~/assets/css/breadcrumbs.css');
</style>
